// Pure selection-state logic for the live wall's operator-picked camera set.
//
// Kept framework-free and DOM-free so the cap arithmetic, the seed rule, and
// the storage-validation rules get reviewed and tested on their own — same
// reasoning as deviceTree.ts's own comment on why it is a pure function.

import type { Camera } from "../../lib/api";

/**
 * Parses a persisted selection (from localStorage). Returns null for "never
 * chosen" — either nothing was stored, or what was stored doesn't parse as a
 * plain array of camera ids. Treating an invalid value the same as "never
 * chosen" is deliberate: a validation failure should fall through to the
 * first-run seed, not to a permanently un-reseeded empty wall — `[]` is
 * reserved for "the operator chose nothing" (see seedSelection's caller).
 */
export function parseSelectionJSON(raw: string | null): readonly string[] | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || !parsed.every((v): v is string => typeof v === "string")) return null;
  return dedupe(parsed);
}

/**
 * Parses a `cams=` URL search param — a comma-separated list of camera ids.
 * An empty string means "the operator chose nothing"; distinguishing that
 * from "the param is absent" (never chosen) is the caller's job, since
 * URLSearchParams already tells absent vs. present apart.
 */
export function parseSelectionParam(raw: string): readonly string[] {
  if (raw === "") return [];
  return dedupe(raw.split(",").filter((id) => id.length > 0));
}

export function serializeSelectionParam(selection: readonly string[]): string {
  return selection.join(",");
}

function dedupe(ids: readonly string[]): readonly string[] {
  return [...new Set(ids)];
}

/**
 * Adds or removes one camera from the selection. An add lands at the end —
 * selection order is display order, so a fresh tick lands in the next free
 * window (see LiveGridPage's own comment on why tiles are ordered this way).
 * Untick is always allowed; a tick past `limit` is a no-op that returns the
 * same array reference, so a caller can skip re-rendering on a refused tick.
 */
export function toggleSelection(selection: readonly string[], cameraId: string, limit: number): readonly string[] {
  if (selection.includes(cameraId)) {
    return selection.filter((id) => id !== cameraId);
  }
  if (selection.length >= limit) return selection;
  return [...selection, cameraId];
}

/**
 * Drops any id no longer in `availableIds` (deleted, disabled, or out of the
 * viewer's scope), preserving selection order. Called on every render from
 * the live camera list — the result is NOT written back to storage by
 * itself. A transient refetch gap must not permanently drop a tick; only an
 * explicit user action (toggleSelection) persists.
 */
export function reconcileSelection(selection: readonly string[], availableIds: ReadonlySet<string>): readonly string[] {
  const next = selection.filter((id) => availableIds.has(id));
  return next.length === selection.length ? selection : next;
}

/**
 * The first-run fill: the first `count` enabled cameras, in the order given
 * (the host's display order), clamped to `limit` so a fresh browser never
 * opens already over the cap. `count` of 0 is a legitimate "don't auto-fill"
 * setting — see autoStart.ts's own comment on AUTO_START_STREAMS.
 */
export function seedSelection(enabledCameras: readonly Pick<Camera, "id">[], count: number, limit: number): readonly string[] {
  const n = Math.max(0, Math.min(count, limit, enabledCameras.length));
  return enabledCameras.slice(0, n).map((c) => c.id);
}
